import React from 'react';
import { AlertBanner, InputTextArea, Label, Spinner, Text } from '@spark';
import CopyFieldButton from '@/features/model-setup/components/CopyFieldButton';
import JsonCodeTextArea from '@/features/model-setup/components/JsonCodeTextArea';
import { ComparisonModeOption } from '@/shared/domain/taskTypes';
import {
    formatCommercialModelLabel,
    formatTeacherComparisonStatus,
} from '@/features/model-setup/utils/teacherModelUtils';
import { buildAdvancedSettingsSummaryRows } from '@/features/trainings/utils/advancedSettingsSummary';
import { shouldShowEvaluationNormalization } from '@/features/trainings/utils/trainingUtils';
import { formatDatasetNameList } from '@/features/datasets/utils/datasetCloneNames';
import EvaluationNormalizationSummary from '../EvaluationNormalizationSummary';

/**
 * Display-only prompt field. Uses `disabled` (not `readOnly`) so Spark applies
 * the same gray background and not-allowed cursor as JsonCodeTextArea.
 */
const DisplayPromptTextArea = ({ value, displayValue, className, rows }) => (
    <div className="train-model-summary__field-control">
        <CopyFieldButton value={value} />
        <InputTextArea
            showLabel={false}
            showAsterisk={false}
            showHelpIcon={false}
            value={displayValue ?? value}
            disabled
            placeholder=""
            className={className}
            rows={rows}
        />
    </div>
);

/**
 * Model Summary body for the Train / Model Summary tab (Figma 3001-68512).
 * Two-column layout: prompt fields on the left, datasets + commercial model on
 * the right. Optional fields (system prompt, JSON schema, student overrides)
 * render only when they have content / apply to the current setup.
 * `promptsFooter` renders at the bottom of the prompt column (Retrain / Train
 * modal places Advanced there so it matches the textarea width).
 */
const ModelSummarySection = ({
    loading = false,
    usecase,
    availableProviders,
    teacherSystemPrompt = '',
    teacherPrompt = '',
    teacherSchema = '',
    showStudentSystemPrompt = false,
    showStudentPrompt = false,
    showStudentSchema = false,
    hasMultilabelMismatch = false,
    trainingDevice,
    metricOptimization,
    betaForFScore,
    /** Advanced "Optimize on" pick; `null` / omitted reads as "All labels". */
    rareClassLabel = null,
    predictionStrategy,
    baseModel = null,
    showTitle = true,
    promptsFooter = null,
}) => {
    const isExternalModelResults = [ComparisonModeOption.manual_model_predictions, ComparisonModeOption.all].includes(
        usecase?.comparison_mode_option
    );

    const commercialModelLabel = formatCommercialModelLabel({
        teacherModel: usecase?.teacher_model,
        teacherProvider: usecase?.teacher_provider,
        availableProviders,
    });

    const comparisonStatus = formatTeacherComparisonStatus({
        isActive: Boolean(usecase?.comparison_mode),
        teacherModel: usecase?.teacher_model,
        teacherProvider: usecase?.teacher_provider,
        availableProviders,
    });

    const advancedRows = buildAdvancedSettingsSummaryRows({
        taskType: usecase?.task_type,
        trainingDevice,
        metricOptimization,
        betaForFScore,
        rareClassLabel,
        predictionStrategy,
        baseModel,
        usecase,
    });

    const showSystemPrompt = Boolean(String(teacherSystemPrompt || '').trim());
    const showJsonSchema = Boolean(String(teacherSchema || '').trim());

    return (
        <div className="train-model-summary">
            {showTitle ? (
                <Text variant="lead-semibold" as="div">
                    Model Summary
                </Text>
            ) : null}

            {loading ? (
                <div className="train-model-summary__loading">
                    <Spinner size="large" />
                </div>
            ) : (
                <div className="train-model-summary__grid">
                    <div className="train-model-summary__prompts fs-unmask">
                        {showSystemPrompt && (
                            <div className="train-model-summary__field">
                                <Label label="System Prompt" showAsterisk={false} showIcon={false} />
                                <DisplayPromptTextArea
                                    value={teacherSystemPrompt}
                                    className="train-prompt-textarea"
                                    rows={4}
                                />
                            </div>
                        )}

                        <div className="train-model-summary__field">
                            <Label label="Prompt" showAsterisk={false} showIcon={false} />
                            <DisplayPromptTextArea
                                value={teacherPrompt}
                                displayValue={teacherPrompt || '-'}
                                className={`train-prompt-textarea${showSystemPrompt || showJsonSchema ? '' : ' train-prompt-textarea--hero'}`}
                                rows={showSystemPrompt || showJsonSchema ? 7 : 14}
                            />
                        </div>

                        {showJsonSchema && (
                            <div className="train-model-summary__field">
                                <Label label="JSON Schema" showAsterisk={false} showIcon={false} />
                                <div className="train-model-summary__field-control">
                                    <CopyFieldButton value={teacherSchema} />
                                    <JsonCodeTextArea value={teacherSchema} rows={6} disabled />
                                </div>
                            </div>
                        )}

                        {showStudentSystemPrompt && (
                            <div className="train-model-summary__field">
                                <Label
                                    label="Student System Prompt"
                                    showAsterisk={false}
                                    showIcon={false}
                                />
                                <DisplayPromptTextArea
                                    value={usecase?.generative_student_instructions}
                                    className="train-prompt-textarea"
                                    rows={4}
                                />
                            </div>
                        )}

                        {showStudentPrompt && (
                            <div className="train-model-summary__field">
                                <Label label="Student Prompt" showAsterisk={false} showIcon={false} />
                                <DisplayPromptTextArea
                                    value={usecase?.generative_student_prompt}
                                    className="train-prompt-textarea"
                                    rows={7}
                                />
                            </div>
                        )}

                        {showStudentSchema && (
                            <div className="train-model-summary__field">
                                <Label
                                    label="Student JSON Schema"
                                    showAsterisk={false}
                                    showIcon={false}
                                />
                                <div className="train-model-summary__field-control">
                                    <CopyFieldButton value={usecase?.generative_schema} />
                                    <JsonCodeTextArea
                                        value={usecase?.generative_schema}
                                        rows={6}
                                        disabled
                                    />
                                </div>
                            </div>
                        )}
                        {promptsFooter}
                    </div>

                    <div className="train-model-summary__meta fs-unmask">
                        <div className="train-model-summary__meta-block">
                            <Label label="Datasets Used" showAsterisk={false} showIcon={false} />
                            <div className="train-model-summary__meta-rows">
                                <div className="train-model-summary__meta-row">
                                    <Text variant="detail-regular" color="secondary">
                                        {usecase?.datasets?.length > 1 ? 'Training Sets' : 'Training Set'}
                                    </Text>
                                    <Text variant="detail-regular">
                                        {formatDatasetNameList(usecase?.datasets) || '-'}
                                    </Text>
                                </div>
                                <div className="train-model-summary__meta-row">
                                    <Text variant="detail-regular" color="secondary">
                                        {usecase?.benchmarks?.length > 1 ? 'Benchmarks' : 'Benchmark'}
                                    </Text>
                                    <Text variant="detail-regular">
                                        {formatDatasetNameList(usecase?.benchmarks) || '-'}
                                    </Text>
                                </div>
                                {isExternalModelResults && usecase?.benchmarks?.length > 0 && (
                                    <div className="train-model-summary__meta-row">
                                        <Text variant="detail-regular" color="secondary">
                                            External Model Results
                                        </Text>
                                        <Text variant="detail-regular">
                                            {usecase?.manual_model_predictions?.join(', ') || '-'}
                                        </Text>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="train-model-summary__meta-block train-model-summary__meta-block--model">
                            <Label label="Commercial Model" showAsterisk={false} showIcon={false} />
                            <Text variant="base-regular" color="secondary" as="div">
                                {commercialModelLabel}
                            </Text>
                        </div>

                        <div className="train-model-summary__meta-block train-model-summary__meta-block--model">
                            <Label
                                label="Compare to commercial model"
                                showAsterisk={false}
                                showIcon={false}
                            />
                            <Text variant="base-regular" color="secondary" as="div">
                                {comparisonStatus}
                            </Text>
                        </div>

                        {shouldShowEvaluationNormalization(usecase) ? (
                            <EvaluationNormalizationSummary source={usecase} variant="train" />
                        ) : null}

                        <div className="train-model-summary__meta-block">
                            <Label label="Advanced" showAsterisk={false} showIcon={false} />
                            <div className="train-model-summary__meta-rows">
                                {advancedRows.map((row) => (
                                    <div
                                        key={row.label}
                                        className="train-model-summary__meta-row"
                                    >
                                        <Text variant="detail-regular" color="secondary">
                                            {row.label}
                                        </Text>
                                        <Text variant="detail-regular">{row.value}</Text>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {hasMultilabelMismatch && (
                <div className="train-model-summary__warning fs-unmask">
                    <AlertBanner
                        feedback="warning"
                        text="Multilabel Dataset Detected — You're training a single-label model with multilabel data. Only the first label from each example will be used."
                        showIcon={true}
                        showButton={false}
                        showCloseButton={false}
                    />
                </div>
            )}
        </div>
    );
};

export default ModelSummarySection;
